import React, { useEffect, useMemo, useState } from 'react';

import { useSlider } from '@/hooks/useSlider';
import { Section } from '@/components/global/Section';

import Table from '@/components/global/Table';
import Card from '@/components/global/Card';
import NoMoreItems from '@/components/global/NoMoreItems';
import { Filters } from '@/components/Template/Template8/Filters';
import { useTaxonomy } from '@/components/Template/Template8/useTaxonomy';

import {
  TCard,
  TOrderBy,
  TRecommendation,
} from '@/services/requests/Recommendation/types/template8';

import { applyFilter } from '@/helpers/Filters/basisc-filter';
import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import Sort from '@/components/Template/Template8/Sort';

import { MONTH_NAMES_PT } from './utils';

type TProps = {
  data: TRecommendation[];
};

const TemplateVitrine8 = ({ data }: TProps) => {
  const { filters, cards, no_items, title } = data[0];

  const [filteredCards, setFilteredCards] = useState<TCard[]>([]);

  const prefixTemplateClass = 'vitrine-template8';
  const { images } = useTaxonomy({ cards });

  const { sliderState, moveSliderTo, containerRef } = useSlider(
    { cards } as any,
    '',
    true,
    '8',
    `${prefixTemplateClass}__card`,
  );

  const handleFilterChange = (selectedFilters: Record<string, string>) => {
    const newFilteredCards = filters.cards?.filter(card => {
      return filters.filters_options.every(filterOption => {
        const { card_key } = filterOption;
        const filterValue = selectedFilters[card_key];

        const cardKeyValue = card.cardkey.find(
          item => item.card_key_key === card_key,
        )?.card_key_value;

        if (!cardKeyValue || !filterValue) {
          return true;
        }

        return applyFilter(cardKeyValue, filterValue, filterOption.comparator);
      });
    });

    setFilteredCards(newFilteredCards || []);
  };

  const handleSortChange = (selected: TOrderBy['options'][0]) => {
    // card key = chave para ordenar
    // order by = tipo de ordenação (ascendente, descendente)
    const { card_key, order_by } = selected;

    const sortedCards = [...filteredCards].sort((a, b) => {
      const aCardKey = a.cardkey.find(item => item.card_key_key === card_key);
      const bCardKey = b.cardkey.find(item => item.card_key_key === card_key);

      if (!aCardKey || !bCardKey) {
        return 0;
      }

      const aVal = aCardKey.card_key_value ?? '';
      const bVal = bCardKey.card_key_value ?? '';

      const aType = (aCardKey.card_key_type || '').toString().toLowerCase();
      const isNumberType = aType === 'número' || aType === 'numero';

      if (isNumberType) {
        const aNum = parseFloat(String(aVal).replace(',', '.')) || 0;
        const bNum = parseFloat(String(bVal).replace(',', '.')) || 0;
        return order_by === 'Ascendente' ? aNum - bNum : bNum - aNum;
      }

      if (order_by === 'Ascendente') {
        return String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      } else {
        return String(bVal).localeCompare(String(aVal), undefined, { numeric: true, sensitivity: 'base' });
      }
    });

    setFilteredCards(sortedCards);
  }

  useEffect(() => {
    if (data.length > 0 && data[0].filters?.cards) {
      setFilteredCards(data[0].filters.cards);
    }
  }, [data]);

  const getFilterOptions = (key: string) => {
    const options = data[0].filters.cards?.map((card, index) => {
      const cardKey = card.cardkey.find(k => k.card_key_key === key);

      if (cardKey) {
        return {
          index,
          label: cardKey.card_key_value,
          value: cardKey.card_key_value,
        }
      }

      return undefined;
    }).filter((card): card is { index: number; label: any; value: any } => card !== undefined) || [];

    const seen = new Set<string>();
    return options.filter(opt => {
      const normalized = String(opt.value ?? '').trim().toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }

  const departureOptions = useMemo(() => {
    return getFilterOptions('departure');
  }, [data[0].filters.cards]);

  const arrivalOptions = useMemo(() => {
    return getFilterOptions('arrival');
  }, [data[0].filters.cards]);

  const monthsOptions = useMemo(() => {
    const months = getFilterOptions('months');

    const sorted = months.sort((a, b) => {
      const monthA = a.label ? parseInt(a.label) : 13;
      const monthB = b.label ? parseInt(b.label) : 13;
      return monthA - monthB;
    });

    return sorted.map(month => {
      const monthIndex = month.label ? parseInt(month.label) - 1 : -1;

      return {
        ...month,
        label: monthIndex >= 0 && monthIndex < 12 ? MONTH_NAMES_PT[monthIndex] : month.label,
      }
    });
  }, [data[0].filters.cards]);

  console.log('CARDS', cards);

  return (
    <Section className="vitrine-template8" data={data}>
      <div className="vitrine-template8__container">
        <div className="vitrine-template8__cards-wrapper">
          <div className="vitrine-template8__cards" ref={containerRef}>
            {cards.length === 0 && typeof no_items !== 'string' && (
              <NoMoreItems
                template="8"
                noitems={{
                  image: no_items.image,
                  title: no_items.title,
                  description: no_items.description,
                  channel: no_items.channel,
                  resolution: no_items.resolution,
                }}
              />
            )}
            {cards.map((card, index) => (
              <Card
                key={card.id}
                template="8"
                cards={{
                  type: 'card',
                  header_1: card.header_1,
                  header_2: card.header_2,
                  header_icon: card.header_icon,
                  image: {
                    image: {
                      url:
                        images[index] ||
                        card.image.image?.url ||
                        card.image.tag ||
                        '',
                    },
                  },
                  card_text: card.card_text,
                  card_text_text: card.card_text_text,
                  card_link: card.card_link,
                  tags_config: card.tags_config,
                  card_text_tag_text: card.card_text_tag_text,
                  card_text_tag_color: card.card_text_tag_color,
                }}
              />
            ))}
          </div>

          {/* Dots */}
          <div className="vitrine-template8__carousel-dots">
            {cards.map((card, i) => (
              <span
                key={`dot-${card.id}`}
                role="button"
                aria-hidden="true"
                tabIndex={0}
                onClick={() => moveSliderTo(i)}
                className={`vitrine-template8__carousel-dot${i + 1 === sliderState.current
                  ? ' vitrine-template8__carousel-dot--active'
                  : ''
                  }`}
              />
            ))}
          </div>
        </div>

        <div className="vitrine-template8__filtered-table">
          <h3 className="vitrine-template8__filtered-table--title">
            {clearHtmlTags(title)}
          </h3>
          <Filters
            onFilterChange={handleFilterChange}
            isDisabled={filters.cards?.length === 0}
            options={filters.filters_options}
            departureOptions={departureOptions}
            arrivalOptions={arrivalOptions}
            monthsOptions={monthsOptions}
          />
          <div className="vitrine-template8__filtered-table__info">
            <tgr-text-description>{filteredCards.length} {data[0].result_quantity}</tgr-text-description>
            <Sort
              title={data[0].order_by.title}
              options={data[0].order_by.options}
              onSortChange={handleSortChange}
            />
          </div>
          <Table
            template="8"
            column_header={data[0].column_header}
            cards={filteredCards}
          />
        </div>
      </div>
    </Section>
  );
};

export default TemplateVitrine8;
